/**
 * Money off a bill.
 *
 * A discount is priced when it is applied and recorded with who applied it and
 * why, so the till sends what was asked for — "20%", "£5" — and the server
 * says what that came to. Nothing here works the amount out locally: two
 * answers to what a guest pays is one too many.
 *
 * The reasons are the restaurant's own list, and the till reads it rather than
 * offering a fixed set: "staff meal" and "it took too long" are different
 * lines in anybody's accounts.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiError } from './client';

import { useApi } from './provider';

export interface DiscountReason {
  id: string;
  code: string;
  label: string;
  /** Null: no ceiling. Otherwise the most this reason may take, as a percentage. */
  max_percent: string | null;
  requires_note: boolean;
  is_active: boolean;
}

export interface DiscountReasonSuggestion {
  code: string;
  label: string;
  requires_note: boolean;
}

/**
 * A service charge rule: a percentage added to the bills it reaches.
 *
 * The till never reads these. The server adds the charge to a tab when a rule
 * matches and the tab says what it added, so the rule and the bill cannot
 * disagree.
 */
export interface ServiceChargeRule {
  id: string;
  /** Null: every site. */
  location_id: string | null;
  /** What the guest reads on the bill. */
  name: string;
  percent: string;
  /** Null: every party. */
  min_guests: number | null;
  table_service_only: boolean;
  /** The tax class it is taxed in. Null: not taxed. */
  tax_class: string | null;
  is_active: boolean;
}

export interface ServiceChargeRuleInput {
  location_id?: string | null;
  name?: string;
  percent?: string;
  min_guests?: number | null;
  table_service_only?: boolean;
  tax_class?: string | null;
  is_active?: boolean;
}

export interface DiscountInput {
  orderId: string;
  /** Omitted or null: off the whole bill. */
  line_id?: string | null;
  reason_code: string;
  kind: 'percent' | 'amount';
  /** A percentage, or an amount of money, depending on `kind`. */
  value: string;
  note?: string;
}

export function useDiscountReasons(options?: { includeRetired?: boolean }) {
  const api = useApi();
  const retired = options?.includeRetired ?? false;
  return useQuery({
    queryKey: ['pricing', 'discount-reasons', retired],
    queryFn: ({ signal }) =>
      api.get<DiscountReason[]>(
        `/v1/pricing/discount-reasons?include_retired=${retired}`,
        signal,
      ),
  });
}

export function useDiscountReasonSuggestions() {
  const api = useApi();
  return useQuery({
    queryKey: ['pricing', 'discount-reasons', 'suggestions'],
    queryFn: ({ signal }) =>
      api.get<DiscountReasonSuggestion[]>('/v1/pricing/discount-reasons/suggestions', signal),
    staleTime: 60 * 60_000,
  });
}

function useReasonMutation<TInput>(
  send: (api: ReturnType<typeof useApi>, input: TInput) => Promise<unknown>,
) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<unknown, ApiError, TInput>({
    mutationFn: (input) => send(api, input),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['pricing', 'discount-reasons'] });
    },
  });
}

/** 409 on a code that is taken, or one that is not a code. */
export function useAddDiscountReason() {
  return useReasonMutation<{
    code: string;
    label: string;
    max_percent?: string | null;
    requires_note?: boolean;
  }>((api, input) => api.post<DiscountReason>('/v1/pricing/discount-reasons', input));
}

/** Reword, cap, or retire. Retiring keeps it on every discount that names it. */
export function useUpdateDiscountReason() {
  return useReasonMutation<{
    id: string;
    label?: string;
    max_percent?: string | null;
    requires_note?: boolean;
    is_active?: boolean;
  }>((api, { id, ...body }) =>
    api.request<DiscountReason>(`/v1/pricing/discount-reasons/${id}`, {
      method: 'PATCH',
      body,
    }),
  );
}

function useOrderDiscountMutation<TInput extends { orderId: string }>(
  send: (api: ReturnType<typeof useApi>, input: TInput) => Promise<unknown>,
) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<unknown, ApiError, TInput>({
    mutationFn: (input) => send(api, input),
    onSettled: (_data, _error, input) => {
      void queryClient.invalidateQueries({ queryKey: ['orders'] });
      void queryClient.invalidateQueries({ queryKey: ['orders', 'detail', input.orderId] });
    },
  });
}

/**
 * 403 without the permission — a discount is not a waiter's to give — and 409
 * when a reason needs a note, a ceiling is crossed, or it is more than is left
 * on the bill.
 */
export function useApplyDiscount() {
  return useOrderDiscountMutation<DiscountInput>((api, { orderId, ...body }) =>
    api.post<{ id: string }>(`/v1/orders/${orderId}/discounts`, body),
  );
}

/** Every rule, switched-off ones included. Professional; 402 below it. */
export function useServiceChargeRules(options?: { enabled?: boolean }) {
  const api = useApi();
  return useQuery({
    queryKey: ['pricing', 'service-charges'],
    queryFn: ({ signal }) =>
      api.get<ServiceChargeRule[]>('/v1/pricing/service-charges', signal),
    enabled: options?.enabled ?? true,
  });
}

function useServiceChargeRuleMutation<TInput>(
  send: (api: ReturnType<typeof useApi>, input: TInput) => Promise<unknown>,
) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<unknown, ApiError, TInput>({
    mutationFn: (input) => send(api, input),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['pricing', 'service-charges'] });
      // Open tabs are re-priced by the server when a rule changes.
      void queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
  });
}

/** Applies at once, to the tabs already open as well. */
export function useAddServiceChargeRule() {
  return useServiceChargeRuleMutation<
    ServiceChargeRuleInput & { name: string; percent: string }
  >((api, input) => api.post<ServiceChargeRule>('/v1/pricing/service-charges', input));
}

/**
 * Only what is sent changes. Null clears a nullable field: every site, every
 * party, not taxed.
 */
export function useUpdateServiceChargeRule() {
  return useServiceChargeRuleMutation<ServiceChargeRuleInput & { id: string }>(
    (api, { id, ...body }) =>
      api.request<ServiceChargeRule>(`/v1/pricing/service-charges/${id}`, {
        method: 'PATCH',
        body,
      }),
  );
}

/**
 * Take the service charge off one tab. 403 without the permission, 409 with
 * no reason or when there is no charge on the tab.
 */
export function useRemoveServiceCharge() {
  return useOrderDiscountMutation<{ orderId: string; reason: string }>((api, input) =>
    api.post<unknown>(`/v1/orders/${input.orderId}/service-charge/remove`, {
      reason: input.reason,
    }),
  );
}

/** Put it back, at what the rule makes it on the bill as it is now. */
export function useRestoreServiceCharge() {
  return useOrderDiscountMutation<{ orderId: string }>((api, input) =>
    api.post<unknown>(`/v1/orders/${input.orderId}/service-charge/restore`, {}),
  );
}

export function useRemoveDiscount() {
  return useOrderDiscountMutation<{ orderId: string; discountId: string }>((api, input) =>
    api.request<void>(`/v1/orders/${input.orderId}/discounts/${input.discountId}`, {
      method: 'DELETE',
    }),
  );
}

/**
 * A promotion: money off chosen dishes at the times the restaurant says.
 *
 * `percent` or `amount` off each dish, each dish at a set `price`, or
 * `buy_get` — buy N, get M free or at a percentage off (the cheapest in each
 * set). The till never reads these: the server takes them off a tab every
 * time it is re-totalled, and the tab names them.
 */
export interface Promotion {
  id: string;
  name: string;
  kind: 'percent' | 'amount' | 'price' | 'buy_get';
  /** The percentage, the amount or the price. Null on buy-and-get. */
  value: string | null;
  buy_qty: number | null;
  get_qty: number | null;
  /** Off each of the ones that come with them. 100 is free. */
  get_percent: string | null;
  /** Both empty: every dish. */
  item_ids: string[];
  category_ids: string[];
  /** Monday=0 … Sunday=6. Empty: every day. */
  days: number[];
  /** The site's clock, "HH:MM:SS". Both null: all day. */
  starts_at: string | null;
  ends_at: string | null;
  valid_from: string | null;
  valid_to: string | null;
  /** Null: every site. */
  location_id: string | null;
  channel: 'any' | 'table' | 'counter';
  /** Dishes, or sets on buy-and-get, per bill. Null: no limit. */
  max_per_bill: number | null;
  /** Only when a coupon gives it. */
  coupon_only: boolean;
  is_active: boolean;
}

export type PromotionInput = Omit<Promotion, 'id' | 'is_active'> & { is_active?: boolean };

/** Every promotion, switched-off ones included. Professional; 402 below it. */
export function usePromotions(options?: { enabled?: boolean }) {
  const api = useApi();
  return useQuery({
    queryKey: ['pricing', 'promotions'],
    queryFn: ({ signal }) => api.get<Promotion[]>('/v1/pricing/promotions', signal),
    enabled: options?.enabled ?? true,
  });
}

function usePromotionMutation<TInput>(
  send: (api: ReturnType<typeof useApi>, input: TInput) => Promise<unknown>,
) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<unknown, ApiError, TInput>({
    mutationFn: (input) => send(api, input),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['pricing', 'promotions'] });
      void queryClient.invalidateQueries({ queryKey: ['pricing', 'coupons'] });
      // Open tabs are re-priced by the server when a promotion changes.
      void queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
  });
}

/** Applies at once, to the tabs already open as well. 409 names the field. */
export function useAddPromotion() {
  return usePromotionMutation<PromotionInput>((api, input) =>
    api.post<Promotion>('/v1/pricing/promotions', input),
  );
}

/** Sent whole: every field is an answer, "every day" and "no limit" included. */
export function useReplacePromotion() {
  return usePromotionMutation<PromotionInput & { id: string }>((api, { id, ...body }) =>
    api.request<Promotion>(`/v1/pricing/promotions/${id}`, { method: 'PUT', body }),
  );
}

export function useSwitchPromotion() {
  return usePromotionMutation<{ id: string; is_active: boolean }>((api, { id, is_active }) =>
    api.request<Promotion>(`/v1/pricing/promotions/${id}`, {
      method: 'PATCH',
      body: { is_active },
    }),
  );
}

/**
 * A coupon: what a code handed to a guest is worth. Either a promotion, which
 * then applies to the bill whether or not it runs on its own, or an amount
 * off the whole bill.
 */
export interface Coupon {
  id: string;
  name: string;
  kind: 'promotion' | 'amount';
  promotion_id: string | null;
  promotion_name: string | null;
  amount: string | null;
  valid_from: string | null;
  valid_to: string | null;
  /** Bills one code may go on. Null: no limit. */
  uses_per_code: number | null;
  /** Times one guest may use it. Null: no limit, and no guest need be named. */
  uses_per_guest: number | null;
  /** What the dishes must come to after meal deals and promotions. */
  min_spend: string | null;
  is_active: boolean;
  /** How many codes, and the code itself when there is only one. */
  codes: number;
  code: string | null;
  /** Bills it is on now, and what it has taken off them. */
  used: number;
  taken_off: string;
}

export interface CouponTerms {
  name: string;
  kind: 'promotion' | 'amount';
  promotion_id?: string | null;
  amount?: string | null;
  valid_from?: string | null;
  valid_to?: string | null;
  uses_per_code?: number | null;
  uses_per_guest?: number | null;
  min_spend?: string | null;
}

/** One code a guest used on one tab. */
export interface CouponRedemption {
  id: string;
  coupon_id: string;
  code: string;
  order_id: string;
  order_number: number;
  business_date: string;
  location_id: string;
  /** The guest named on the tab, if any. */
  customer_id: string | null;
  redeemed_by: string;
  redeemed_at: string;
  amount: string;
  note: string | null;
  /** Set when it came off the tab, or the tab was cancelled. */
  released_at: string | null;
  released_by: string | null;
  released_reason: string | null;
}

export function useCoupons(options?: { enabled?: boolean }) {
  const api = useApi();
  return useQuery({
    queryKey: ['pricing', 'coupons'],
    queryFn: ({ signal }) => api.get<Coupon[]>('/v1/pricing/coupons', signal),
    enabled: options?.enabled ?? true,
  });
}

export function useCouponRedemptions(couponId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ['pricing', 'coupons', couponId ?? null, 'redemptions'],
    queryFn: ({ signal }) =>
      api.get<CouponRedemption[]>(`/v1/pricing/coupons/${couponId}/redemptions`, signal),
    enabled: Boolean(couponId),
  });
}

function useCouponMutation<TInput>(
  send: (api: ReturnType<typeof useApi>, input: TInput) => Promise<unknown>,
) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<unknown, ApiError, TInput>({
    mutationFn: (input) => send(api, input),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['pricing', 'coupons'] });
    },
  });
}

/** One `code`, or a `batch` of unique ones made by the server. */
export function useAddCoupon() {
  return useCouponMutation<CouponTerms & { code?: string; batch?: number; prefix?: string }>(
    (api, input) => api.post<Coupon>('/v1/pricing/coupons', input),
  );
}

export function useReplaceCoupon() {
  return useCouponMutation<CouponTerms & { id: string }>((api, { id, ...body }) =>
    api.request<Coupon>(`/v1/pricing/coupons/${id}`, { method: 'PUT', body }),
  );
}

/** Withdraw, or bring back. Tabs that already have it keep it. */
export function useSwitchCoupon() {
  return useCouponMutation<{ id: string; is_active: boolean }>((api, { id, is_active }) =>
    api.request<Coupon>(`/v1/pricing/coupons/${id}`, { method: 'PATCH', body: { is_active } }),
  );
}

/** Where a coupon's codes are downloaded from, for `useDownload`. */
export function couponCodesPath(couponId: string): string {
  return `/v1/pricing/coupons/${couponId}/codes.csv`;
}

/**
 * Take a coupon code on a tab. 409 with the reason in words — unknown,
 * withdrawn, out of date, used up, not this guest's, the bill too small, or
 * a coupon already on the bill.
 */
export function useApplyCoupon() {
  return useOrderDiscountMutation<{ orderId: string; code: string }>((api, input) =>
    api.post<unknown>(`/v1/orders/${input.orderId}/coupons`, { code: input.code }),
  );
}

/** Take it off the tab. The code is given back. */
export function useRemoveCoupon() {
  return useOrderDiscountMutation<{ orderId: string; couponId: string }>((api, input) =>
    api.request<unknown>(`/v1/orders/${input.orderId}/coupons/${input.couponId}`, {
      method: 'DELETE',
    }),
  );
}
